import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/store';
import { filterSectionsByUser } from '../../utils/sidebarConfig';

interface SidebarItem {
  title: string;
  path: string;
  method?: string;
  active?: boolean;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

interface SidebarProps {
  sections: SidebarSection[];
  isOpen?: boolean;
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ sections, isOpen = true, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  console.log('user',user)
  const [searchQuery, setSearchQuery] = useState('');

  const handleItemClick = (path: string) => {
    navigate(path);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  // Filter sections based on user email restrictions
  const filteredSectionsByUser = React.useMemo(
    () => filterSectionsByUser(sections, user?.email),
    [sections, user?.email]
  );

  // Filter sections and items based on search query
  const filteredSections = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return filteredSectionsByUser;
    }

    const query = searchQuery.toLowerCase();
    
    return filteredSectionsByUser
      .map(section => ({
        ...section,
        items: section.items.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.method?.toLowerCase().includes(query) ||
          section.title.toLowerCase().includes(query)
        )
      }))
      .filter(section => section.items.length > 0);
  }, [filteredSectionsByUser, searchQuery]);

  const getMethodBadgeStyles = (method?: string) => {
    if (!method) return '';
    
    const baseStyles = 'inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide mr-3 flex-shrink-0';
    
    switch (method.toUpperCase()) {
      case 'GET':
        return `${baseStyles} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`;
      case 'POST':
        return `${baseStyles} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400`;
      case 'PUT':
        return `${baseStyles} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400`;
      case 'PATCH':
        return `${baseStyles} bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400`;
      case 'DELETE':
      case 'DEL':
        return `${baseStyles} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`;
      default:
        return `${baseStyles} bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300`;
    }
  };

  return (
    <>
      {/* Hamburger button when closed - at top */}
      {!isOpen && onToggle && (
        <button
          onClick={onToggle}
          className="fixed top-20 left-2 z-50 w-8 h-8 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/80 text-white rounded-md transition-all duration-200 flex items-center justify-center shadow-lg"
          aria-label="Open sidebar"
        >
          <div className="w-4 h-4 flex flex-col justify-center items-center">
            <div className="w-4 h-0.5 bg-white mb-0.5"></div>
            <div className="w-4 h-0.5 bg-white mb-0.5"></div>
            <div className="w-4 h-0.5 bg-white"></div>
          </div>
        </button>
      )}

      {isOpen && (
        <aside className="w-80 bg-[var(--bg-primary)] border-r border-[var(--border-color)] h-full flex flex-col">
          {/* Fixed Header with Search, Close Button and Mode Tabs */}
          <div className="flex-shrink-0 bg-[var(--bg-primary)] border-b border-[var(--border-color)]">
            <div className="p-4 space-y-3">
              <div className="flex items-center space-x-2">
                {onToggle && (
                  <button
                    onClick={onToggle}
                    className="w-8 h-8 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/80 text-white rounded-md transition-all duration-200 flex items-center justify-center flex-shrink-0"
                    aria-label="Close sidebar"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <form onSubmit={handleSearchSubmit} className="relative flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full px-4 py-2 pl-10 pr-12 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <kbd className="px-2 py-1 text-xs font-mono text-[var(--text-muted)] bg-[var(--bg-tertiary)] rounded border border-[var(--border-color)]">⌘K</kbd>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {filteredSections.length > 0 ? (
          filteredSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-2">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide px-2">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item, itemIndex) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={itemIndex}>
                      <button
                        className={`w-full flex items-center px-2 py-2.5 rounded-md text-left transition-all duration-150 ${
                          isActive
                            ? 'bg-[var(--accent-color)]/10 text-[var(--accent-color)] border-l-2 border-[var(--accent-color)]'
                            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] border-l-2 border-transparent'
                        }`}
                        onClick={() => handleItemClick(item.path)}
                      >
                        {item.method && (
                          <span className={getMethodBadgeStyles(item.method)}>
                            {item.method.toUpperCase() === 'DELETE' ? 'DEL' : item.method.toUpperCase()}
                          </span>
                        )}
                        <span className="text-sm font-medium truncate">{item.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        ) : (
          <div className="text-center py-8 px-4">
            <svg className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-[var(--text-secondary)] text-sm">No results found for "{searchQuery}"</p>
            <p className="text-[var(--text-muted)] text-xs mt-1">Try a different search term</p>
          </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
};
